import { graphqlClient } from '@/services/core/graphqlClient';
import { GRAPHQL_RESOURCES } from '@/services/resources';

// Type definitions for miscellaneous data
export interface DiagnosisCause {
    id: string;
    name: string;
    code?: string;
    category?: string;
}

export interface InsuranceOption {
    id: string;
    name: string;
    type: string;
    isActive: boolean;
}

export interface FilterOption {
    id: string;
    label: string;
    value: string;
    category: string;
}

/**
 * MiscGraphqlService - Handles miscellaneous GraphQL operations
 * Consumes organized queries from miscGquery.ts
 */
export class MiscGraphqlService {
    /**
     * Get diagnosis causes
     */
    async getDiagnosisCauses(hasuraToken: string): Promise<Array<{
        id: number;
        cause: string;
    }>> {
        const result = await graphqlClient.query(
            GRAPHQL_RESOURCES.GET_DIAGNOSIS_CAUSES,
            {},
            hasuraToken
        );
        return result.data?.api_prescriptioncauselist || [];
    }

    /**
     * Get primary diagnosis options
     */
    async getPrimaryDxOptions(): Promise<any[]> {
        const result = await graphqlClient.query(
            GRAPHQL_RESOURCES.GET_PRIMARY_DX_OPTIONS
        );
        return result.data?.pep_primary_dx_options || [];
    }

    /**
     * Get secondary diagnosis options
     */
    async getSecondaryDxOptions(): Promise<any[]> {
        const result = await graphqlClient.query(
            GRAPHQL_RESOURCES.GET_SECONDARY_DX_OPTIONS
        );
        return result.data?.pep_secondary_dx_options || [];
    }

    /**
     * Get primary insurance options
     */
    async getPrimaryInsuranceOptions(): Promise<any[]> {
        const result = await graphqlClient.query(
            GRAPHQL_RESOURCES.GET_PRIMARY_INSURANCE_OPTIONS
        );
        return result.data?.pep_primary_insurance_options || [];
    }

    /**
     * Get combined filter options
     */
    async getCombinedFilterOptions(): Promise<any[]> {
        const result = await graphqlClient.query(
            GRAPHQL_RESOURCES.GET_COMBINED_FILTER_OPTIONS
        );
        return result.data?.combined_filter_options || [];
    }

    /**
     * Get insurance dependent options
     */
    async getInsuranceDependentOptions(): Promise<any[]> {
        const result = await graphqlClient.query(
            GRAPHQL_RESOURCES.GET_INSURANCE_DEPENDENT_OPTIONS
        );
        return result.data?.pep_insurance_dependent_options || [];
    }

    /**
     * Get primary diagnosis list
     */
    async getPrimaryDxList(): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.PRIMARY_DX_LIST
      );
      return result.data?.pep_vw_consolidated_data || [];
    }
  
    /**
     * Get secondary diagnosis list
     */
    async getSecondaryDxList(): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.SECONDARY_DX_LIST
      );
      return result.data?.pep_vw_consolidated_data || [];
    }
  
    /**
     * Get primary insurance providers
     */
    async getPrimaryInsuranceProviders(): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.PRIMARY_INSURANCE_PROVIDERS
      );
      return result.data?.pep_vw_consolidated_data || [];
    }
  
    /**
     * Get secondary insurance providers
     */
    async getSecondaryInsuranceProviders(): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.SECONDARY_INSURANCE_PROVIDERS
      );
      return result.data?.pep_vw_consolidated_data || [];
    }
  
    /**
     * Apply simple filters
     */
    async applyFiltersSimple(clinicId: number): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.APPLY_FILTERS_SIMPLE,
        { clinicId }
      );
      return result.data?.pep_vw_consolidated_data || [];
    }
  
    /**
     * Apply complex filters
     */
    async applyFiltersComplex(filterParams: {
      clinicId: number;
      facilityIds?: number[];
      providerIds?: number[];
      primaryList?: string[];
      secondaryList?: string[];
      primaryInsList?: string[];
      secondaryInsList?: string[];
      insuranceType?: string[];
      formattedDate?: string;
    }): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.APPLY_FILTERS_COMPLEX,
        filterParams
      );
      return result.data?.pep_vw_consolidated_data || [];
    }
  
    /**
     * Get diagnosis causes (simple version)
     */
    async getDiagnosisCausesSimple(): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_DIAGNOSIS_CAUSES_SIMPLE
      );
      return result.data?.api_prescriptioncauselist || [];
    }

    /**
     * Get prescription cause list
     */
    async getPrescriptionCauseList(hasuraToken: string): Promise<Array<{
      active: boolean;
      cause: string;
    }>> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_PRESCRIPTION_CAUSE_LIST,
        {},
        hasuraToken
      );
      return result.data?.api_prescriptioncauselist || [];
    }

    /**
     * Get SMS status values
     */
    async getSMSStatusValues(hasuraToken: string): Promise<Array<{
      id: number;
      status: string;
    }>> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.SMS_STATUS_VALUES,
        {},
        hasuraToken
      );
      return result.data?.api_sms || [];
    }

    /**
     * Check if patient has opted out of SMS communications
     */
    async checkSMSOptOut(patientId: number | string, hasuraToken: string): Promise<Array<{
      patient_id: number;
      is_deleted: boolean;
      phone_number: string;
      reason: string;
      key: string;
      id: number;
      created_at: string;
      clinic_phone_number: string;
      updated_at: string;
    }>> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.SMS_OPT_OUT_CHECK,
        { patient_id: String(patientId) },
        hasuraToken
      );
      return result.data?.api_sms_opt_out || [];
    }

    /**
     * Get insurance providers from emr_patient_insurance
     */
    async getInsuranceProviders(hasuraToken: string): Promise<Array<{
      insurance_payor: string;
      insurance_id: number;
    }>> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_INSURANCE_PROVIDERS,
        {},
        hasuraToken
      );
      return result.data?.emr_patient_insurance || [];
    }
  
    /**
     * Get all insurance options combined
     */
    async getAllInsuranceOptions(): Promise<{
      primary: any[];
      secondary: any[];
      dependent: any[];
    }> {
      const [primary, secondary, dependent] = await Promise.all([
        this.getPrimaryInsuranceProviders(),
        this.getSecondaryInsuranceProviders(),
        this.getInsuranceDependentOptions()
      ]);
  
      return {
        primary,
        secondary,
        dependent
      };
    }

    /**
     * Get all diagnosis options combined
     */
    async getAllDiagnosisOptions(): Promise<{
      causes: any[];
      causesSimple: any[];
      primary: any[];
      secondary: any[];
      primaryList: any[];
      secondaryList: any[];
    }> {
      const [causes, causesSimple, primary, secondary, primaryList, secondaryList] = await Promise.all([
        this.getDiagnosisCauses(''), // This method needs hasuraToken but this combined method doesn't have it
        this.getDiagnosisCausesSimple(),
        this.getPrimaryDxOptions(),
        this.getSecondaryDxOptions(),
        this.getPrimaryDxList(),
        this.getSecondaryDxList()
      ]);
  
      return {
        causes,
        causesSimple,
        primary,
        secondary,
        primaryList,
        secondaryList
      };
    }

    /**
     * Get states list
     */
    async getStates(hasuraToken: string): Promise<Array<{
      id: number;
      state_name: string;
      state_code: string;
    }>> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_STATES,
        {},
        hasuraToken
      );
      return result.data?.api_state || [];
    }
}

// Export singleton instance
export const miscGraphqlService = new MiscGraphqlService();